import React, { useState, useEffect } from "react";
import { Loader, Statistic, Table, Dropdown, Icon } from "semantic-ui-react";
import { groupBy, map, orderBy, sumBy } from "lodash";
import moment from "moment";
import {
  HeaderPage,
  PrintHeader, PrintTable,
  TablePayments,
  TablePaymentsPrintColumns,
} from "../../components/Admin";
import { usePayment, usePrintOrientation } from "../../hooks";
import { getBusinessToday, getBusinessDaysAgo, getBusinessDate } from "../../utils/date";
import { getTableLabel, getTableNumber } from "../../utils/table";

const VIEW_OPTIONS = [
  { key: "summary", value: "summary", text: "Resumido" },
  { key: "detailed", value: "detailed", text: "Detallado" },
];


export function LossesReport() {
  const [dateFrom, setDateFrom] = useState(() => getBusinessDaysAgo(30));
  const [dateTo, setDateTo] = useState(getBusinessToday);
  const [viewMode, setViewMode] = useState("summary");
  const { loading, payments, getLossesInRange } = usePayment();
  usePrintOrientation("portrait");

  useEffect(() => {
    if (dateFrom && dateTo) getLossesInRange(dateFrom, dateTo);
  }, [dateFrom, dateTo]);

  const totalLoss = sumBy(payments, (payment) => Number(payment.totalPayment));

  // Agrupado por (fecha, mesa) — no solo por mesa — para poder ordenar el
  // reporte cronológicamente. La fecha usa moment.parseZone (no moment() a
  // secas) para tomar el día tal como viene en el timestamp con offset de
  // Lima, sin que la hora del navegador lo corra a otro día calendario; y
  // se trunca a solo el día (se ignora la hora) porque lo que importa acá
  // es "qué día se liberó la mesa", no el minuto exacto.
  const byDateAndTable = map(
    groupBy(
      payments,
      (payment) =>
        `${getBusinessDate(payment.created_at).format("YYYY-MM-DD")}-${
          getTableLabel(payment)
        }`
    ),
    (groupPayments) => ({
      date: getBusinessDate(groupPayments[0].created_at).format("YYYY-MM-DD"),
      number: getTableNumber(groupPayments[0]),
      label: getTableLabel(groupPayments[0]),
      total: sumBy(groupPayments, (payment) => Number(payment.totalPayment)),
      count: groupPayments.length,
    })
  );
  const sortedByTable = orderBy(
    byDateAndTable,
    ["date", "count", "number"],
    ["desc", "desc", "asc"]
  );

  const dateRangeLabel = `Del ${moment(dateFrom).format("DD/MM/YYYY")} al ${moment(
    dateTo
  ).format("DD/MM/YYYY")}`;

  return (
    <>
      <div className="screen-only">
        <HeaderPage
          title="Reporte de Pérdidas"
          btnTitle={
            <>
              <Icon name="print" /> Imprimir
            </>
          }
          btnClick={() => window.print()}
        />
      </div>

      <div
        className="screen-only"
        style={{ display: "flex", gap: "15px", margin: "10px 0 20px" }}
      >
        <div>
          <label style={{ display: "block", marginBottom: 4 }}>Desde</label>
          <input
            type="date"
            value={dateFrom}
            max={dateTo}
            onChange={(e) => setDateFrom(e.target.value)}
          />
        </div>
        <div>
          <label style={{ display: "block", marginBottom: 4 }}>Hasta</label>
          <input
            type="date"
            value={dateTo}
            min={dateFrom}
            onChange={(e) => setDateTo(e.target.value)}
          />
        </div>
        <div>
          <label style={{ display: "block", marginBottom: 4 }}>Tipo de reporte</label>
          <Dropdown
            selection
            options={VIEW_OPTIONS}
            value={viewMode}
            onChange={(_, data) => setViewMode(data.value)}
          />
        </div>
      </div>

      <PrintTable>
        <PrintHeader title="Reporte de Pérdidas" subtitle={dateRangeLabel} />

        {loading ? (
          <Loader active inline="centered">
            Cargando...
          </Loader>
        ) : payments?.length === 0 ? (
          <p>No hay pérdidas registradas en el rango de fechas seleccionado.</p>
        ) : (
          <div className="print-content">
            <Statistic.Group widths="two" size="small" style={{ marginBottom: "25px" }}>
              <Statistic color="red">
                <Statistic.Value>S/ {totalLoss.toFixed(2)}</Statistic.Value>
                <Statistic.Label>Total perdido</Statistic.Label>
              </Statistic>
              <Statistic color="orange">
                <Statistic.Value>{payments.length}</Statistic.Value>
                <Statistic.Label>Mesas liberadas</Statistic.Label>
              </Statistic>
            </Statistic.Group>
          </div>
        )}
      </PrintTable>

      {!loading && payments?.length > 0 && (
        <>
          <PrintTable>
            <PrintHeader
              title="Reporte de Pérdidas"
              subtitle={`${dateRangeLabel} — Pérdidas por mesa`}
            >
              <div className="print-header-admin__columns">
                <span>Fecha</span>
                <span>Mesa</span>
                <span>Veces liberada</span>
                <span>Total perdido</span>
              </div>
            </PrintHeader>

            <div className="print-content">
              <h4 className="screen-only">Pérdidas por mesa</h4>
              <Table compact style={{ marginBottom: "30px" }}>
                <Table.Header className="screen-only">
                  <Table.Row>
                    <Table.HeaderCell>Fecha</Table.HeaderCell>
                    <Table.HeaderCell>Mesa</Table.HeaderCell>
                    <Table.HeaderCell>Veces liberada</Table.HeaderCell>
                    <Table.HeaderCell>Total perdido</Table.HeaderCell>
                  </Table.Row>
                </Table.Header>
                <Table.Body>
                  {map(sortedByTable, (row) => (
                    <Table.Row key={`${row.date}-${row.label}`}>
                      <Table.Cell>{moment(row.date).format("DD/MM/YYYY")}</Table.Cell>
                      <Table.Cell>{row.label}</Table.Cell>
                      <Table.Cell>{row.count}</Table.Cell>
                      <Table.Cell>S/ {row.total.toFixed(2)}</Table.Cell>
                    </Table.Row>
                  ))}
                </Table.Body>
              </Table>
            </div>
          </PrintTable>

          <PrintTable>
            <PrintHeader title="Reporte de Pérdidas" subtitle={`${dateRangeLabel} — Detalle`}>
              <TablePaymentsPrintColumns />
            </PrintHeader>

            <div className="print-content">
              <h4 className="screen-only">Detalle</h4>
              <TablePayments payments={payments} detailed={viewMode === "detailed"} />
            </div>
          </PrintTable>
        </>
      )}
    </>
  );
}
